import React, { useState, useEffect } from 'react';
import CustomExpansionPanel from '../CustomizedComponents/CustomExpansionPanel';
import PropTypes from 'prop-types';
import useAppContext from '../../hooks/context/useAppContext';
import NoData from '../CustomizedComponents/DisplayData/NoData';
import { FormattedMessage } from 'react-intl';
import ContractDetail from './Detail/ContractDetail';
import DeliveryVolumes from './Volumes/DeliveryVolumes';
import PurchaseLastDays from './PurchaseLastDays/PurchaseLastDays';
import Parameters from './Parameters/Parameters';
import { useIntl } from 'react-intl';
import CustomSnackBar from '../CustomizedComponents/CustomSnackbar';
import styled from 'styled-components';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import { useParams } from 'react-router-dom';

const LoadingWrapper = styled.div`
  height: 2px;
`;

const ContractWrapper = ({ id, contract, loading, error, handlers }) => {
  const { customersEnum, postLoading, putLoading } = contract;

  const showNothingToDisplay = Object.keys(contract).length === 0 && !loading && !error && !customersEnum && id !== 'new';
  const showError = !!error && id !== 'new';
  const showBody = (!error && !loading && customersEnum.length > 0) || id === 'new';

  return (
    <>
      {showNothingToDisplay && (
        <NoData>
          <FormattedMessage id={'app.dataTable.body.noData'} />
        </NoData>
      )}
      {showError && (
        <NoData>
          <FormattedMessage id={'app.dataTable.body.noData'} />
        </NoData>
      )}
      {(loading || putLoading || postLoading) && (
        <LoadingWrapper>
          <LoadingIndicator />
        </LoadingWrapper>
      )}
      {showBody && <ContractBody id={id} contract={contract} loading={loading} error={error} handlers={handlers} />}
    </>
  );
};

ContractWrapper.propTypes = {
  contract: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  handlers: PropTypes.object,
  editable: PropTypes.bool,
  setEditable: PropTypes.func,
  id: PropTypes.any,
  isNew: PropTypes.bool,
  handler: PropTypes.any,
  idPanel: PropTypes.any,
};

export default ContractWrapper;

const getIdPanel = (idPanel) => {
  switch (idPanel) {
    case 'basic-information':
      return 1;
    case 'volumes':
      return 2;
    case 'params':
      return 4;
    default:
      return 1;
  }
};

const ContractBody = ({ id, contract, loading, error, handlers }) => {
  const { getEnums, setBackNavigation } = useAppContext();
  const enums = getEnums();
  const intl = useIntl();
  const { idPanel } = useParams();
  const { contractPeriod, commodityName } = contract;
  const [editable, setEditable] = useState(true);
  const [activePannelId, setActivePannelId] = useState(getIdPanel(idPanel));
  const [isNew, setIsNew] = useState(false);
  const purchaseLastDaysTitle = 'app.contract.purchaseLastDays';
  const contractTitle = 'app.contract';
  const editIcon = 'app.icon.edit';
  const volume = 'app.contract.volume';
  const success = intl.formatMessage({ id: 'app.action.saved' });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(success);
  const [issues, setIssues] = useState(null);
  const [expandedState, setExpandedState] = useState(false);

  const { postLoadingContract, putLoadingContract, postError, putError } = contract;

  const { sendContractState } = handlers;
  const { stateLoading, stateError, stateData } = contract;

  const handleChange = (changeId) => {
    setActivePannelId(activePannelId === changeId ? 0 : changeId);
  };

  useEffect(() => {
    if (activePannelId !== 0) {
      setExpandedState(true);
    } else {
      setExpandedState(false);
    }
  }, [activePannelId, setExpandedState]);

  useEffect(() => {
    if (id !== 'new') {
      setBackNavigation(`/contract/${id}`);
    } else {
      setBackNavigation(null);
    }
  }, [setBackNavigation, id]);

  useEffect(() => {
    if (id === 'new') {
      setEditable(true);
      setIsNew(true);
    }
  }, [id, isNew]);

  useEffect(() => {
    if (putError) {
      setMessage(putError?.data?.message);
      setIssues(putError?.data?.issues);
    } else if (postError) {
      setMessage(postError?.data?.message);
      setIssues(postError?.data?.issues);
    } else {
      setMessage(success);
      setIssues(null);
    }
  }, [putError, postError, success]);

  useEffect(() => {
    if (stateData && !stateError && !stateLoading) {
      setMessage(success);
      setOpen(true);
    }
  }, [stateData, stateError, stateLoading, success]);

  return (
    <>
      {(loading || putLoadingContract || postLoadingContract) && (
        <LoadingWrapper>
          <LoadingIndicator />
        </LoadingWrapper>
      )}
      <CustomExpansionPanel
        id={1}
        text={isNew ? { title: contractTitle, icon: editIcon } : { title: contractTitle, icon: editIcon, texts: [commodityName, contractPeriod] }}
        active={activePannelId === 1}
        handler={handleChange}
        isNew={isNew}
        setIsNew={setIsNew}
        editable={editable}
        setEditable={setEditable}
        expandedState={expandedState}>
        <ContractDetail
          idPanel={2}
          id={id}
          loading={loading}
          error={error}
          contract={contract}
          enums={enums}
          isNew={isNew}
          handler={handleChange}
          handlers={handlers}
          editable={editable}
          setEditable={setEditable}
          setOpen={setOpen}
          changeContractState={sendContractState}
        />
      </CustomExpansionPanel>

      <CustomExpansionPanel
        id={2}
        text={{ title: volume, icon: editIcon }}
        active={activePannelId === 2}
        isNew={isNew}
        setIsNew={setIsNew}
        handler={handleChange}
        editable={editable}
        setEditable={setEditable}
        expandedState={expandedState}>
        <DeliveryVolumes
          idPanel={3}
          id={id}
          loading={loading}
          error={error}
          contract={contract}
          isNew={isNew}
          handler={handleChange}
          handlers={handlers}
          editable={editable}
          setEditable={setEditable}
          setOpen={setOpen}
        />
      </CustomExpansionPanel>
      <CustomExpansionPanel
        id={3}
        text={{ title: purchaseLastDaysTitle, icon: editIcon }}
        active={activePannelId === 3}
        isNew={isNew}
        setIsNew={setIsNew}
        handler={handleChange}
        editable={editable}
        setEditable={setEditable}
        expandedState={expandedState}>
        <PurchaseLastDays
          id={id}
          idPanel={4}
          handler={handleChange}
          loading={loading}
          error={error}
          contract={contract}
          isNew={isNew}
          handlers={handlers}
          editable={editable}
          setEditable={setEditable}
          setOpen={setOpen}
          isPurchaseLastDaysTabActive={activePannelId === 3}
        />
      </CustomExpansionPanel>
      <CustomExpansionPanel
        id={4}
        text={{ title: 'app.contract.gradualBuy.params', icon: 'app.icon.edit' }}
        active={activePannelId === 4}
        isNew={isNew}
        setIsNew={setIsNew}
        handler={handleChange}
        editable={editable}
        setEditable={setEditable}
        expandedState={expandedState}>
        <Parameters
          id={id}
          loading={loading}
          error={error}
          contract={contract}
          enums={enums}
          isNew={isNew}
          handlers={handlers}
          editable={editable}
          setEditable={setEditable}
          setOpen={setOpen}
        />
      </CustomExpansionPanel>
      {!postLoadingContract && !putLoadingContract && message && (
        <CustomSnackBar status={putError || postError ? 'error' : 'success'} message={message} setOpen={setOpen} open={open} />
      )}
      {!postLoadingContract &&
        !putLoadingContract &&
        issues &&
        issues.map((issue) => <CustomSnackBar key={issue} status={'error'} message={issue} setOpen={setOpen} open={open} />)}
    </>
  );
};

ContractBody.propTypes = {
  id: PropTypes.any,
  contract: PropTypes.object,
  loading: PropTypes.any,
  error: PropTypes.any,
  handlers: PropTypes.object,
};

ContractBody.propTypes = {};
